import { Suspense, useRef } from 'react';
import { BrowserRouter, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'components/Loader';
import AuthenticationRoute from 'components/AuthenticationRoute';
import NotFound404 from 'auth/404';
import appRoutes from 'config/appRoutes';
import { loadUser } from 'store/container/auth/auth-slice';
import { setVersionUpdate } from 'store/container/versionUpdate/versionUpdate-slice';
import { lazyRetry } from 'utils/lazyRetry';
import { setSentryUser } from '../sentry';
import { RootState } from '../store';
import useWebSocket from '../utils/useWebSocket';
import AuthClient from '../auth/AuthClient';
import 'styles/global.less';

const Groups = lazyRetry(() => import('modules/groups/routes'));
const ApiTokens = lazyRetry(() => import('modules/api-tokens/routes'));
const Clients = lazyRetry(() => import('modules/client/routes'));
const Dashboard = lazyRetry(() => import('modules/dashboard/routes'));
const Contacts = lazyRetry(() => import('modules/contact/routes'));
const InternalUsers = lazyRetry(() => import('modules/contact/views/InternalUsers'));
const ContactsList = lazyRetry(() => import('modules/contact/views/ContactsList'));
const Customers = lazyRetry(() => import('modules/customer/routes'));
const Requests = lazyRetry(() => import('modules/request/routes'));
const Projects = lazyRetry(() => import('modules/project/routes'));
const Inbox = lazyRetry(() => import('modules/inbox/routes'));
const PendingReplies = lazyRetry(() => import('modules/pending-replies/routes'));
const Tasks = lazyRetry(() => import('modules/tasks/routes'));
const Content = lazyRetry(() => import('modules/content/routes'));
const TimelogReport = lazyRetry(() => import('modules/timelogReport/routes'));
const Report = lazyRetry(() => import('modules/report/routes'));
const FileViewer = lazyRetry(() => import('modules/fileViewer/routes'));
const HowTo = lazyRetry(() => import('modules/how-to/routes'));
const TeamActivity = lazyRetry(() => import('modules/team-activity/routes'));
const TeamActivityTasks = lazyRetry(() => import('modules/team-activity-tasks/routes'));

const Login = lazyRetry(() => import('auth/Login'));
const Register = lazyRetry(() => import('auth/Register'));
const ForgotPassword = lazyRetry(() => import('auth/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('auth/ResetPassword'));
const PrivacyPolicy = lazyRetry(() => import('app/PrivacyPolicy'));
const TermsOfServices = lazyRetry(() => import('app/TermsOfServices'));

const Routing = () => {
  const auth = useSelector((store: RootState) => store.auth);
  const isMountedRef = useRef(false);
  const dispatch = useDispatch();

  if (!isMountedRef.current) {
    isMountedRef.current = true;
    dispatch(loadUser());
  }

  if (auth.user) {
    setSentryUser(auth.user);
  }

  useWebSocket({
    channelName: 'events',
    privateChannel: false,
    listen: {
      event: '.version.update',
      callback: (event) => {
        if (event.env === 'admin') {
          dispatch(setVersionUpdate());
        }
      },
    },
  });

  if (auth.user && auth.user.contact.type === 'contact') {
    return <AuthClient />;
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<Loader />}>
        <Switch>
          <AuthenticationRoute component={Dashboard} path={appRoutes.DASHBOARD} exact isAuth auth={auth} />
          <AuthenticationRoute component={Groups} path={appRoutes.GROUPS} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={ApiTokens} path={appRoutes.API_TOKENS} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={Clients} path={appRoutes.CLIENTS} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={Customers} path={appRoutes.CUSTOMERS} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={InternalUsers} path={appRoutes.INTERNAL_USERS} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={ContactsList} path={appRoutes.CONTACTS_LIST} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={Contacts} path={appRoutes.CONTACTS} isAuth auth={auth} />
          <AuthenticationRoute component={Requests} path={appRoutes.REQUESTS} isAuth auth={auth} overflowScroll />
          <AuthenticationRoute component={Inbox} path={appRoutes.INBOX} isAuth auth={auth} />
          <AuthenticationRoute component={PendingReplies} path={appRoutes.PENDING_REPLIES} isAuth auth={auth} />
          <AuthenticationRoute component={TimelogReport} path={appRoutes.TIME_LOG_REPORT} isAuth auth={auth} />
          <AuthenticationRoute
            component={Projects}
            path={appRoutes.PROJECTS}
            isAuth
            auth={auth}
            type="regular_user"
            overflowScroll
          />
          <AuthenticationRoute component={Content} path={appRoutes.CONTENT} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={Tasks} path={appRoutes.TASKS} isAuth auth={auth} />
          <AuthenticationRoute component={Report} path={appRoutes.REPORT} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={FileViewer} path={`${appRoutes.FILE_VIEWER}/:uuid`} isAuth auth={auth} noLayout />
          <AuthenticationRoute component={HowTo} path={appRoutes.HOW_TO} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute component={TeamActivity} path={appRoutes.TEAM_ACTIVITY} isAuth auth={auth} type="regular_user" />
          <AuthenticationRoute
            component={TeamActivityTasks}
            path={appRoutes.TEAM_ACTIVITY_TASKS}
            isAuth
            auth={auth}
            type="regular_user"
          />

          <AuthenticationRoute component={Login} path={appRoutes.LOGIN} exact isAuth={false} auth={auth} />
          <AuthenticationRoute component={Register} path={appRoutes.REGISTER} exact isAuth={false} auth={auth} />
          <AuthenticationRoute component={ForgotPassword} path={appRoutes.FORGOT_PASSWORD} exact isAuth={false} auth={auth} />

          <AuthenticationRoute component={TermsOfServices} path={appRoutes.TERMS} exact isAuth={null} noLayout />
          <AuthenticationRoute component={PrivacyPolicy} path={appRoutes.PRIVACY} exact isAuth={null} noLayout />
          <AuthenticationRoute component={ResetPassword} path={appRoutes.RESET} exact isAuth={null} noLayout />

          <AuthenticationRoute component={NotFound404} path="*" isAuth={null} auth={auth} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  );
};

export default Routing;
